/**
 * Process Status Types and Constants
 * Note: 'recieved' is intentionally misspelled to match the API
 */

// Process status states (keeping API typo for compatibility)
export type ProcessStatus =
  | 'recieved'
  | 'picking'
  | 'packing'
  | 'packed'
  | 'dispatched'
  | 'delivered'
  | 'returned'
  | 'picking_error'
  | 'cancelled'

// Process status configuration
export const PROCESS_STATUS_LABELS: Record<ProcessStatus, string> = {
  recieved: 'Předat k vyskladnění',
  picking: 'Předat k zabalení',
  packing: 'Zabaleno',
  packed: 'Předat dopravci',
  dispatched: 'Doručeno',
  delivered: 'Vráceno',
  returned: 'Vráceno',
  picking_error: 'Chyba při vyskladnění',
  cancelled: 'Zrušeno',
}

export const EARLY_STATES: ProcessStatus[] = ['recieved', 'picking', 'packing']
export const LATE_STATES: ProcessStatus[] = ['packed', 'dispatched', 'delivered', 'cancelled']
export const ALL_PROCESS_STATUSES: ProcessStatus[] = [
  'recieved',
  'picking',
  'packing',
  'packed',
  'dispatched',
  'delivered',
  'returned',
  'picking_error',
  'cancelled',
]

// Timing constants
export const PROCESS_STATUS_DELAY = {
  DEFAULT: 300,
  ERROR: 5000,
  PRINT_WAIT: 250,
} as const

// Error messages
export const ERROR_MESSAGES = {
  UNPICKED_ITEMS: 'Chyba: nenapickovane polozky!',
  UNPACKED_ITEMS: 'Chyba: nezabalene polozky!',
  PRINT_ERROR: 'Chyba při tisku štítků',
  UPDATE_ERROR: 'Chyba při aktualizaci stavu objednávky',
  CARRIER_ERROR: 'Chyba dopravce',
  CANCEL_RESERVATION: 'Opravdu zrusit rezervaci?',
  MISSING_CARRIER: 'Není nastaven dopravce!',
  MISSING_SHIPPING_ADDRESS: 'Není nastavena doručovací adresa!',
  COMPLETE_INFO: 'Doplňte informace a pokračujte manuálním posunem stavu',
  AUTO_UPDATE_BLOCKED:
    'Stav objednávky nemůže být automaticky posunut dále, protože objednávka nemá zadaný způsob doručení.',
} as const

// Success messages
export const SUCCESS_MESSAGES = {
  STATUS_UPDATED: 'Stav objednávky aktualizován!',
} as const

// Toast configuration
export const TOAST_CONFIG = {
  WARNING_AUTO_CLOSE: 10000,
} as const

// Order data interfaces (based on the Fragment structure)
export interface OrderProcessStatus {
  id: string
  name?: string
}

export interface OrderCarrier {
  id: string
  name?: string
  requiredShippingAddress?: boolean
}

export interface OrderAddress {
  id: string
}

export interface ClearanceItem {
  id: string
  picked: number
  amount: number
}

export interface OrderItem {
  id: string
  type: string
  packed: boolean
}

export interface OrderPackage {
  id: string
  sent: boolean
  ticketUrl?: string
  carrier?: {
    id: string
    name?: string
  }
}

export interface ProcessStatusRelation {
  status?: { id?: string | null } | null
  created?: string | null
}

export interface ProcessStatusOrder {
  id: string
  processStatus?: OrderProcessStatus | null
  processStatusRelations?: (ProcessStatusRelation | null)[] | null
  carrier?: OrderCarrier | null
  shippingAddress?: OrderAddress | null
  clearanceItems?: ClearanceItem[] | null
  items?: OrderItem[] | null
  packages?: OrderPackage[] | null
}

// Hook result interface
export interface UseProcessStatusResult {
  currentProcessStatus: ProcessStatus
  canUpdateProcessStatus: boolean
  btnNextStatusLabel: string
  backSteps: ProcessStatus[]
  isLoading: boolean
  updateProcessStatus: (nextStatus: ProcessStatus, note?: string) => Promise<void>
  // Status the order had before it was marked as picking_error
  statusBeforeError: ProcessStatus
  restoreStatusBeforeError: () => Promise<void>
  cancelReservation: () => Promise<void>
}
